import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImageUploadField from "@/components/admin/ImageUploadField";

const EMPTY = { name: "", dealer_type: "Authorized Dealer", city: "", phone: "", address: "", website: "", image_url: "", order: 0 };
const inputCls = "h-11 w-full border border-black/20 bg-white px-3 text-sm outline-none focus:border-gavra";
const labelCls = "font-mono-hw text-[11px] uppercase tracking-widest text-black/50";

export default function AdminDealers() {
  const [dealers, setDealers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [nameError, setNameError] = useState("");

  const load = () => api.get("/dealers").then((r) => setDealers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: dealers.length }); setOpen(true); };
  const openEdit = (d) => { setEditing(d.id); setForm({ ...EMPTY, ...d }); setOpen(true); };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) { setNameError("Dealer name is required."); toast.error("Please fill in the required fields."); return; }
    setNameError("");
    setSaving(true);
    const payload = { ...form, order: parseInt(form.order) || 0 };
    try {
      if (editing) await api.put(`/dealers/${editing}`, payload);
      else await api.post("/dealers", payload);
      toast.success(editing ? "Dealer updated" : "Dealer created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/dealers/${deleteId}`);
      toast.success("Dealer deleted");
      setDeleteId(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-dealers">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono-hw text-[11px] uppercase tracking-[0.25em] text-gavra">Where To Buy</p>
          <h1 className="mt-2 font-heading text-4xl font-black uppercase tracking-tight md:text-5xl">Dealers</h1>
        </div>
        <button data-testid="add-dealer-btn" onClick={openNew} className="flex items-center gap-2 bg-black px-5 py-3 text-[12px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-gavra">
          <Plus className="h-4 w-4" /> Add dealer
        </button>
      </div>

      <div className="mt-8 overflow-x-auto border border-black/10">
        <table className="w-full min-w-[720px] text-left">
          <thead className="border-b border-black/10 bg-secondary">
            <tr className="font-mono-hw text-[10px] uppercase tracking-widest text-black/50">
              <th className="p-4">Name</th>
              <th className="p-4">Type</th>
              <th className="p-4">City</th>
              <th className="p-4">Phone</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/10">
            {dealers.map((d) => (
              <tr key={d.id} data-testid={`dealer-row-${d.id}`} className="hover:bg-secondary/50">
                <td className="p-4 font-heading font-bold">{d.name}</td>
                <td className="p-4 text-sm">{d.dealer_type}</td>
                <td className="p-4 text-sm">{d.city}</td>
                <td className="p-4 font-mono-hw text-xs">{d.phone}</td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`edit-dealer-${d.id}`} onClick={() => openEdit(d)} className="border border-black/20 p-2 hover:border-gavra hover:text-gavra">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button data-testid={`delete-dealer-${d.id}`} onClick={() => setDeleteId(d.id)} className="border border-black/20 p-2 hover:border-destructive hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {dealers.length === 0 && (
              <tr><td colSpan={5} className="p-10 text-center text-sm text-black/40">No dealers yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-none border-black/10" data-testid="dealer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black uppercase tracking-tight">
              {editing ? "Edit dealer" : "New dealer"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className={labelCls}>Name *</label>
              <input data-testid="df-name" value={form.name} onChange={(e) => { set("name")(e); setNameError(""); }} className={`mt-1.5 ${inputCls} ${nameError ? "border-destructive" : ""}`} />
              {nameError && <p data-testid="df-name-error" className="mt-1 text-xs text-destructive">{nameError}</p>}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Type</label>
                <input data-testid="df-type" value={form.dealer_type} onChange={set("dealer_type")} className={`mt-1.5 ${inputCls}`} placeholder="Authorized Dealer" />
              </div>
              <div>
                <label className={labelCls}>City / Country</label>
                <input data-testid="df-city" value={form.city} onChange={set("city")} className={`mt-1.5 ${inputCls}`} placeholder="Jakarta, ID" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Address</label>
              <input data-testid="df-address" value={form.address} onChange={set("address")} className={`mt-1.5 ${inputCls}`} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Phone</label>
                <input data-testid="df-phone" value={form.phone} onChange={set("phone")} className={`mt-1.5 ${inputCls}`} />
              </div>
              <div>
                <label className={labelCls}>Website</label>
                <input data-testid="df-website" value={form.website} onChange={set("website")} className={`mt-1.5 ${inputCls}`} placeholder="https://…" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Display order</label>
              <input data-testid="df-order" type="number" value={form.order} onChange={set("order")} className={`mt-1.5 ${inputCls}`} />
            </div>
            <ImageUploadField label="Logo / photo (optional)" testid="df-image" value={form.image_url} onChange={(v) => setForm((f) => ({ ...f, image_url: v }))} />
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="border border-black px-6 py-3 text-[12px] font-bold uppercase tracking-[0.15em] hover:bg-black hover:text-white">Cancel</button>
              <button data-testid="df-save" type="submit" disabled={saving} className="bg-black px-6 py-3 text-[12px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-gavra disabled:opacity-50">
                {saving ? "Saving…" : "Save dealer"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="rounded-none border-black/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase">Delete dealer?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-dealer" onClick={confirmDelete} className="rounded-none bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
